import type { DataSourceEntity } from '@unifyapps/defs/types/dataSource';
import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import type { InterfacePageEntity } from '@unifyapps/defs/types/page';
import type { EmbeddedDependentsResponse } from '@unifyapps/network/generated/models/embeddedDependentsResponse';
import type { Entity } from '@unifyapps/network/generated/models/entity';
import { DEPLOYED_ENTITY_TYPES, STANDARD_ENTITY_TYPES } from './interfaceQueryFilters';

// Entity carries an `entityType` discriminator (UAEntityTypes value) so we can split
// the bag of dependents into the buckets consumers expect (datasources / pages / interface records).
type EntityWithType = Entity & { id?: string };

export type NormalizedEmbeddedDependents = {
  // The entity the call was keyed on - for our usage this is always a Component (page)
  interfacePage: InterfacePageEntity | undefined;
  // Map keyed by datasource id; matches the shape of the legacy useFetchDataSourcesByComponentId hook
  dataSources: Record<string, DataSourceEntity | undefined>;
  // Only populated by the deep `embedded-entities` variant; nested page entities discovered via traversal
  embeddedPages: Record<string, InterfacePageEntity | undefined>;
  // Interface records for embedded pages discovered via traversal, keyed by interface id
  embeddedInterfaceRecords: Record<string, InterfaceEntity | undefined>;
};

const EMPTY: NormalizedEmbeddedDependents = {
  interfacePage: undefined,
  dataSources: {},
  embeddedPages: {},
  embeddedInterfaceRecords: {},
};

/**
 * Normalises an EmbeddedDependentsResponse into the buckets the no-code runtime
 * expects. keyBasedDependents are entities embedded by id reference (this is where
 * data sources for the page live); referencedDependents are loose references that
 * may include nested pages discovered by the deep `embedded-entities` traversal,
 * as well as the interface records those pages belong to.
 *
 * Pass `isDeployed: true` when normalising a response sourced from the deployed
 * endpoints (useSearchDeployedInterfacePageEmbeddedEntities /
 * prefetchSearchDeployedInterfacePageEmbeddedEntities); those entities use the
 * `*_deployed` entityType discriminators.
 */
export function normalizeEmbeddedDependents(
  resp: EmbeddedDependentsResponse | undefined,
  isDeployed = false,
): NormalizedEmbeddedDependents {
  if (!resp) return EMPTY;

  const entityTypes = isDeployed ? DEPLOYED_ENTITY_TYPES : STANDARD_ENTITY_TYPES;

  const interfacePage =
    (resp.entity as EntityWithType | undefined)?.entityType === entityTypes.Component
      ? (resp.entity as unknown as InterfacePageEntity)
      : undefined;

  const dataSources: Record<string, DataSourceEntity | undefined> = {};
  // Change embeddedPages from InterfacePageEntity[] to Record<string, InterfacePageEntity | undefined>
  const embeddedPages: Record<string, InterfacePageEntity | undefined> = {};
  const embeddedInterfaceRecords: Record<string, InterfaceEntity | undefined> = {};

  const all = [...(resp.keyBasedDependents ?? []), ...(resp.referencedDependents ?? [])];
  for (const ent of all as EntityWithType[]) {
    if (!ent.id) continue;
    if (ent.entityType === entityTypes.DataSource) {
      dataSources[ent.id] = ent as unknown as DataSourceEntity;
    } else if (ent.entityType === entityTypes.Component) {
      embeddedPages[ent.id] = ent as unknown as InterfacePageEntity;
    } else if (ent.entityType === entityTypes.Interface) {
      embeddedInterfaceRecords[ent.id] = ent as unknown as InterfaceEntity;
    }
  }

  return { interfacePage, dataSources, embeddedPages, embeddedInterfaceRecords };
}

/**
 * Combines the root page with embedded pages, filtering out any undefined/id-less entries.
 */
export function getAllPagesFromEmbedded(
  interfacePage: InterfacePageEntity | undefined,
  embeddedPages: Record<string, InterfacePageEntity | undefined>,
): InterfacePageEntity[] {
  return Object.values({
    ...(interfacePage ? { [interfacePage.id]: interfacePage } : {}),
    ...embeddedPages,
  }).filter((p): p is InterfacePageEntity => Boolean(p?.id));
}
